import type { ReactNode } from 'react'
import Icon, { type IconName } from '../../docs/Icon'
import Carousel from '../ui/Carousel'

// Live examples for the content components. A mini specimen is a still
// preview for the catalog: no ids, nothing focusable.

type SpecimenProps = { mini?: boolean }

function Still({ children }: { children: ReactNode }) {
  return <div className="lib-framed lib-still">{children}</div>
}

// Every icon in the set; the type check fails when one is added or removed.
const iconNames = Object.keys({
  arrow: true,
  search: true,
  grid: true,
  code: true,
  check: true,
  layers: true,
  close: true,
  menu: true,
  download: true,
  external: true,
  chevron: true,
  copy: true,
  spark: true,
  clock: true,
  file: true,
  info: true,
  sun: true,
  moon: true,
  sliders: true,
  play: true,
  pause: true,
  pen: true,
  plus: true,
  reset: true,
  star: true,
  lock: true,
  landmark: true,
  globe: true,
  calendar: true,
  upload: true,
  sort: true,
  filter: true,
  sidebar: true,
  share: true,
} satisfies Record<IconName, true>) as IconName[]

function ListSpecimen({ mini }: SpecimenProps) {
  if (mini)
    return (
      <Still>
        <ul className="mds-list">
          <li>Current passport</li>
          <li>One passport photo</li>
        </ul>
        <ol className="mds-list">
          <li>Fill out the form</li>
          <li>Pay the fee</li>
        </ol>
      </Still>
    )
  return (
    <div className="lib-stack">
      <ul className="mds-list">
        <li>Your current passport</li>
        <li>
          One passport photo
          <ul>
            <li>Taken in the last six months</li>
            <li>Two by two inches, in color</li>
          </ul>
        </li>
        <li>A check or money order for the fee</li>
      </ul>
      <ol className="mds-list">
        <li>Fill out the renewal form.</li>
        <li>Sign and date it.</li>
        <li>Mail it with your passport and photo.</li>
      </ol>
      <ul className="mds-list mds-list--unstyled">
        <li>Monday to Friday, 8 a.m. to 5 p.m.</li>
        <li>Closed on federal holidays</li>
      </ul>
    </div>
  )
}

function ListGroupSpecimen({ mini }: SpecimenProps) {
  const tab = mini ? -1 : undefined
  const items = [
    ['Overview', '#overview', ''],
    ['How to apply', '#apply', '4 steps'],
    ['Check your status', '#status', ''],
  ]
  return (
    <div className="lib-stack">
      <ul className="mds-list-group">
        {items.slice(0, mini ? 2 : 3).map(([title, href, meta], i) => (
          <li key={href}>
            <a className="mds-list-group__item" href={href} aria-current={i === 1 ? 'page' : undefined} tabIndex={tab}>
              {title}
              {meta && <span className="mds-list-group__meta">{meta}</span>}
            </a>
          </li>
        ))}
      </ul>
      {!mini && (
        <ul className="mds-list-group">
          <li className="mds-list-group__item">
            Application received <span className="mds-list-group__meta">Oct 2</span>
          </li>
          <li className="mds-list-group__item">
            In review <span className="mds-list-group__meta">Oct 9</span>
          </li>
          <li className="mds-list-group__item">
            Decision mailed <span className="mds-list-group__meta">Oct 16</span>
          </li>
        </ul>
      )}
    </div>
  )
}

function IconSpecimen({ mini }: SpecimenProps) {
  if (mini)
    return (
      <Still>
        <span className="lib-icon-grid lib-icon-grid--mini">
          {iconNames.slice(0, 12).map((name) => (
            <Icon key={name} name={name} />
          ))}
        </span>
      </Still>
    )
  return (
    <div className="lib-stack">
      <div className="lib-icon-uses">
        <figure className="lib-icon-use">
          <button type="button" className="mds-icon-button" aria-label="Download your form">
            <Icon name="download" />
          </button>
          <figcaption className="mds-muted">
            An icon on its own is a button with a label, here “Download your form”, which screen readers announce.
          </figcaption>
        </figure>
        <figure className="lib-icon-use">
          <span className="lib-icon-use__status">
            Application
            <Icon name="check" size={16} />
            <span className="mds-visually-hidden">received</span>
          </span>
          <figcaption className="mds-muted">
            An icon that carries meaning has words beside it, visible or hidden, so “Application received” is read
            aloud.
          </figcaption>
        </figure>
      </div>
    </div>
  )
}

function IconListSpecimen({ mini }: SpecimenProps) {
  const needs = ['A current passport', 'One passport photo', 'The renewal fee']
  if (mini)
    return (
      <Still>
        <ul className="mds-icon-list">
          {needs.slice(0, 2).map((need) => (
            <li key={need}>
              <span className="mds-icon-list__icon">
                <Icon name="check" size={18} />
              </span>
              {need}
            </li>
          ))}
        </ul>
      </Still>
    )
  const ways: [IconName, string, string][] = [
    ['globe', 'Online', 'Renew in about twenty minutes.'],
    ['file', 'By mail', 'Send the form with your passport.'],
    ['landmark', 'In person', 'Visit an acceptance office.'],
  ]
  return (
    <div className="lib-stack">
      <ul className="mds-icon-list">
        {needs.map((need) => (
          <li key={need}>
            <span className="mds-icon-list__icon">
              <Icon name="check" size={18} />
            </span>
            {need}
          </li>
        ))}
      </ul>
      <ul className="mds-icon-list">
        {ways.map(([icon, title, text]) => (
          <li key={title}>
            <span className="mds-icon-list__icon">
              <Icon name={icon} size={18} />
            </span>
            <span>
              <strong>{title}</strong>
              <br />
              <span className="mds-muted">{text}</span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}

function ProseSpecimen({ mini }: SpecimenProps) {
  if (mini)
    return (
      <Still>
        <div className="mds-prose lib-prose-mini">
          <p>
            <strong>Renew by mail</strong>
          </p>
          <p>Most adults can renew by mail if their passport is undamaged.</p>
        </div>
      </Still>
    )
  return (
    <article className="mds-prose">
      <h2>Renew your passport by mail</h2>
      <p>Most adults can renew by mail. Your passport must be undamaged and issued within the last fifteen years.</p>
      <h3>What to send</h3>
      <ul>
        <li>The renewal form, signed and dated</li>
        <li>Your most recent passport</li>
        <li>One passport photo</li>
      </ul>
      <p>
        Routine service takes four to six weeks. <a href="#fees">See fees and processing times</a>.
      </p>
    </article>
  )
}

const programs = [
  ['Benefits', 'Apply for benefits', 'Check eligibility and apply online.'],
  ['Travel', 'Renew a passport', 'Renew by mail or online in about twenty minutes.'],
  ['Records', 'Request your records', 'Get copies of the documents we hold about you.'],
  ['Housing', 'Find rental help', 'See programs in your state.'],
  ['Health', 'Find a clinic', 'Search by ZIP code for care near you.'],
]

function CarouselSpecimen({ mini }: SpecimenProps) {
  if (mini)
    return (
      <Still>
        <span className="lib-fake-carousel">
          {programs.slice(0, 3).map(([tag, title]) => (
            <span key={title} className="mds-card mds-card--outline">
              <span className="mds-tag">{tag}</span>
              <strong>{title}</strong>
            </span>
          ))}
        </span>
      </Still>
    )
  return (
    <Carousel label="Popular services">
      {programs.map(([tag, title, text]) => (
        <article key={title} className="mds-card mds-card--outline mds-card--link">
          <span className="mds-tag">{tag}</span>
          <h3 className="mds-card__title">
            <a className="mds-card__link" href={`#${tag.toLowerCase()}`}>
              {title}
            </a>
          </h3>
          <p className="mds-muted">{text}</p>
        </article>
      ))}
    </Carousel>
  )
}

export const contentSpecimens: Record<string, (props: SpecimenProps) => ReactNode> = {
  list: ListSpecimen,
  'list-group': ListGroupSpecimen,
  icon: IconSpecimen,
  'icon-list': IconListSpecimen,
  prose: ProseSpecimen,
  carousel: CarouselSpecimen,
}

/** Every icon in the set with its name, for the icon page's reference section. */
export function IconLibrary() {
  return (
    <ul className="lib-icon-grid">
      {iconNames.map((name) => (
        <li key={name} className="lib-icon-grid__item">
          <Icon name={name} size={24} />
          <code>{name}</code>
        </li>
      ))}
    </ul>
  )
}
